// Google OAuth Service for E-Commerce Intelligence Module

const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

export interface GoogleConnection {
  id: string;
  platformId: string;
  googleEmail: string;
  googleName: string;
  googlePicture?: string;
  scope: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface AnalyticsProperty {
  accountId: string;
  accountName: string;
  propertyId: string;
  propertyName: string;
}

export interface SearchConsoleSite {
  siteUrl: string;
  permissionLevel: string;
}

export interface MerchantAccount {
  merchantId: string;
  aggregatorId?: string;
}

export interface YouTubeChannel {
  channelId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  subscriberCount?: string;
  videoCount?: string;
}

export interface AdsAccount {
  customerId: string;
  descriptiveName: string;
  currencyCode?: string;
  timeZone?: string;
}

export interface GmailLabel {
  labelId: string;
  name: string;
  type?: string;
  messagesTotal?: number;
  messagesUnread?: number;
}

export interface GoogleCalendar {
  calendarId: string;
  summary: string;
  description?: string;
  timeZone?: string;
  colorId?: string;
  backgroundColor?: string;
  foregroundColor?: string;
  primary?: boolean;
  accessRole?: string;
}

export interface SelectedProperties {
  analytics?: { propertyId: string; propertyName: string; accountId: string; accountName: string }[];
  searchConsole?: { siteUrl: string }[];
  ads?: { customerId: string; descriptiveName: string }[];
  merchant?: { merchantId: string }[];
  youtube?: { channelId: string; title: string }[];
  gmail?: { labelId: string; name: string }[];
  calendar?: { calendarId: string; summary: string }[];
}

/**
 * Initiates Google OAuth flow for a specific platform
 */
export async function initiateGoogleAuth(
  userId: string,
  platformId: string,
  returnUrl?: string
): Promise<string> {
  const params = new URLSearchParams({
    userId,
    platformId,
    returnUrl: returnUrl || window.location.href,
  });

  const response = await fetch(`${FUNCTIONS_URL}/initiateGoogleAuth?${params}`);

  if (!response.ok) {
    throw new Error('Failed to initiate Google authentication');
  }

  const data = await response.json();
  return data.authUrl;
}

/**
 * Opens Google OAuth in a popup window
 */
export function openGoogleAuthPopup(
  userId: string,
  platformId: string
): Promise<boolean> {
  return new Promise(async (resolve, reject) => {
    try {
      const returnUrl = `${window.location.origin}/auth/callback`;
      const authUrl = await initiateGoogleAuth(userId, platformId, returnUrl);

      // Open popup
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'google-auth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );

      if (!popup) {
        // Popup blocked, redirect instead
        window.location.href = authUrl;
        return;
      }

      // Poll for popup close
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          // Check URL params for success
          resolve(true);
        }
      }, 500);

      // Listen for message from popup
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          clearInterval(pollTimer);
          popup.close();
          window.removeEventListener('message', messageHandler);
          resolve(true);
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          clearInterval(pollTimer);
          popup.close();
          window.removeEventListener('message', messageHandler);
          reject(new Error(event.data.error));
        }
      };

      window.addEventListener('message', messageHandler);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Redirects to Google OAuth (for mobile or when popup is blocked)
 */
export async function redirectToGoogleAuth(
  userId: string,
  platformId: string
): Promise<void> {
  const returnUrl = `${window.location.origin}/dashboard/connections`;
  const authUrl = await initiateGoogleAuth(userId, platformId, returnUrl);
  window.location.href = authUrl;
}

/**
 * Gets all Google connections for a user
 */
export async function getGoogleConnections(userId: string): Promise<GoogleConnection[]> {
  const response = await fetch(`${FUNCTIONS_URL}/getGoogleConnections?userId=${userId}`);

  if (!response.ok) {
    throw new Error('Failed to fetch Google connections');
  }

  const data = await response.json();
  return data.connections;
}

/**
 * Gets Google Analytics properties for a connection
 */
export async function getAnalyticsProperties(
  userId: string,
  connectionId: string
): Promise<AnalyticsProperty[]> {
  const params = new URLSearchParams({ userId, connectionId });
  const response = await fetch(`${FUNCTIONS_URL}/getGoogleAnalyticsProperties?${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch Analytics properties');
  }

  const data = await response.json();
  return data.properties;
}

/**
 * Gets Search Console sites for a connection
 */
export async function getSearchConsoleSites(
  userId: string,
  connectionId: string
): Promise<SearchConsoleSite[]> {
  const params = new URLSearchParams({ userId, connectionId });
  const response = await fetch(`${FUNCTIONS_URL}/getSearchConsoleSites?${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch Search Console sites');
  }

  const data = await response.json();
  return data.sites;
}

/**
 * Gets Merchant Center accounts for a connection
 */
export async function getMerchantAccounts(
  userId: string,
  connectionId: string
): Promise<MerchantAccount[]> {
  const params = new URLSearchParams({ userId, connectionId });
  const response = await fetch(`${FUNCTIONS_URL}/getMerchantCenterAccounts?${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch Merchant Center accounts');
  }

  const data = await response.json();
  return data.accounts;
}

/**
 * Gets YouTube channels for a connection
 */
export async function getYouTubeChannels(
  userId: string,
  connectionId: string
): Promise<YouTubeChannel[]> {
  const params = new URLSearchParams({ userId, connectionId });
  const response = await fetch(`${FUNCTIONS_URL}/getYouTubeChannels?${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch YouTube channels');
  }

  const data = await response.json();
  return data.channels;
}

/**
 * Gets Google Ads accounts for a connection
 */
export async function getAdsAccounts(
  userId: string,
  connectionId: string
): Promise<AdsAccount[]> {
  const params = new URLSearchParams({ userId, connectionId });
  const response = await fetch(`${FUNCTIONS_URL}/getGoogleAdsAccounts?${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch Ads accounts');
  }

  const data = await response.json();
  return data.accounts;
}

/**
 * Gets selected properties for a connection
 */
export async function getSelectedProperties(
  userId: string,
  connectionId: string
): Promise<SelectedProperties | null> {
  const params = new URLSearchParams({ userId, connectionId });
  const response = await fetch(`${FUNCTIONS_URL}/getGoogleSelectedProperties?${params}`);

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  return data.selectedProperties || null;
}

/**
 * Saves selected properties for a connection
 */
export async function saveSelectedProperties(
  userId: string,
  connectionId: string,
  selectedProperties: SelectedProperties
): Promise<void> {
  const response = await fetch(`${FUNCTIONS_URL}/saveGoogleSelectedProperties`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, connectionId, selectedProperties }),
  });

  if (!response.ok) {
    throw new Error('Failed to save selected properties');
  }
}

/**
 * Disconnects a Google account
 */
export async function disconnectGoogleAccount(
  userId: string,
  connectionId: string
): Promise<void> {
  const response = await fetch(`${FUNCTIONS_URL}/disconnectGoogle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, connectionId }),
  });

  if (!response.ok) {
    throw new Error('Failed to disconnect Google account');
  }
}

/**
 * Refreshes access token for a connection
 */
export async function refreshGoogleToken(
  userId: string,
  connectionId: string
): Promise<void> {
  const response = await fetch(`${FUNCTIONS_URL}/refreshGoogleToken`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, connectionId }),
  });

  if (!response.ok) {
    throw new Error('Failed to refresh token');
  }
}

/**
 * Check if a platform is a Google platform
 */
export function isGooglePlatform(platformId: string): boolean {
  // Now we only have the unified 'google' platform
  return platformId === 'google';
}

/**
 * Get display name for Google platform
 */
export function getGooglePlatformName(platformId: string): string {
  if (platformId === 'google') {
    return 'Google';
  }
  return platformId;
}

/**
 * List of all Google services included in the unified connection
 */
export const GOOGLE_SERVICES = [
  { id: 'analytics', name: 'Google Analytics 4', icon: 'BarChart3' },
  { id: 'search-console', name: 'Search Console', icon: 'Search' },
  { id: 'merchant', name: 'Merchant Center', icon: 'ShoppingBag' },
  { id: 'ads', name: 'Google Ads', icon: 'Megaphone' },
  { id: 'youtube', name: 'YouTube Analytics', icon: 'Youtube' },
  { id: 'gmail', name: 'Gmail', icon: 'Mail' },
  { id: 'calendar', name: 'Google Calendar', icon: 'Calendar' },
];

/**
 * Gets Gmail labels for a connection
 */
export async function getGmailLabels(
  userId: string,
  connectionId: string
): Promise<GmailLabel[]> {
  const params = new URLSearchParams({ userId, connectionId });
  const response = await fetch(`${FUNCTIONS_URL}/getGmailLabels?${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch Gmail labels');
  }

  const data = await response.json();
  return data.labels || [];
}

/**
 * Gets Google Calendars for a connection
 */
export async function getGoogleCalendars(
  userId: string,
  connectionId: string
): Promise<GoogleCalendar[]> {
  const params = new URLSearchParams({ userId, connectionId });
  const response = await fetch(`${FUNCTIONS_URL}/getGoogleCalendars?${params}`);

  if (!response.ok) {
    throw new Error('Failed to fetch Google Calendars');
  }

  const data = await response.json();
  return data.calendars || [];
}
